import React, { FunctionComponent, ReactNode } from 'react';
import getConfig from 'next/config';
import Head from 'next/head';
import createMarkupFAQ from '../../../utils/createMarkupFAQ';
import { ICategory, IPost, ITag, IAuthor } from '../../../lib/types';
import { getMeta } from '../../../lib/metaGenerator';

const { APP_ENV } = getConfig().publicRuntimeConfig;

interface IProps {
  children?: () => ReactNode;
  title: string;
  url: string;
  post?: IPost;
  category?: ICategory;
  tag?: ITag;
  searchKeyword?: string;
  author?: IAuthor;
  description?: string;
}

/**
 * Include it in all the pages and pass the children as a prop
 * Note: Children should be a function which return html (not as a string)
 */
export const SiteHead: FunctionComponent<IProps> = ({
  title,
  url,
  post,
  category,
  tag,
  author,
  description
}) => {

  const cdnUrls = {
    production: 'https://cdn.lifepal.co.id',
    staging: 'https://cdn.lifepal.tech',
    development: 'https://cdn.lifepal.tech',
  };

  const featuredImages = (postFeaturedImages: IPost): string[] => {
    const images = postFeaturedImages.featuredImage?.node.sourceUrl;
    if (!images) return [];

    const imageSizes: string[] = postFeaturedImages.featuredImage?.node.sourceUrl.split(', ');
    if (!imageSizes.length) return [];
    return imageSizes.map((img) => img.split(' ')[0]);
  };

  const seoDetails = () => {
    const meta = getMeta({ url, post, category, tag, author });

    return (
      <>
        <meta name="description" content={description ?? meta.description} />
        <meta
          name="robots"
          content={`${meta.index}, ${meta.follow}, max-snippet:-1, max-image-preview:large, max-video-preview:-1`}
        />
        <meta property="og:locale" content={meta.openGraph.locale} />
        <meta property="og:type" content={meta.openGraph.type} />
        <meta property="og:title" content={meta.openGraph.title} />
        <meta property="og:description" content={meta.openGraph.description} />
        <meta property="og:url" content={meta.url} />
        <meta property="og:site_name" content={meta.name} />
        <meta property="og:image" content={meta.image} />
        <meta property="og:image:width" content={meta.openGraph.imageWidth as string} />
        <meta property="og:image:height" content={meta.openGraph.imageHeight as string} />
        <meta property="article:publisher" content="https://www.facebook.com/lifepal/" />
        <meta property="article:published_time" content={meta.openGraph.publishedTime} />
        <meta property="article:modified_time" content={meta.openGraph.modifiedTime} />
        <meta name="twitter:card" content={meta.twitter.card} />
        <meta name="twitter:title" content={meta.twitter.title} />
        <meta name="twitter:description" content={meta.twitter.description} />
        <meta name="twitter:image" content={meta.image} />
        <meta name="twitter:creator" content="@lifepal" />
        <meta name="twitter:site" content="@lifepal" />
        <meta name="twitter:label1" content="Written by" />
        <meta name="twitter:data1" content={meta.authorName} />
        <script
          type="application/ld+json"
          className="yoast-schema-graph"
          dangerouslySetInnerHTML={{ __html: meta.schema }}
        />
        {post?.faqs && post?.faqs.length > 0 && <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: createMarkupFAQ(post.faqs)
          }}
        />}
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({ "@context": "https://schema.org/", "@type": "WebSite", "name": "Lifepal.co.id", "url": "https://lifepal.co.id/", "potentialAction": { "@type": "SearchAction", "target": "https://lifepal.co.id/search/?s={search_term_string}", "query-input": "required name=search_term_string" } })
          }}
        />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html:
              JSON.stringify({ "@context": "https://schema.org", "@type": "Organization", "name": "Lifepal.co.id", "url": "https://lifepal.co.id/", "logo": "https://lifepal.co.id/static/images/favicon/favicon.ico", "sameAs": ["https://www.facebook.com/lifepal", "https://twitter.com/lifepal", "https://www.instagram.com/lifepal/", "https://www.youtube.com/c/@lifepalmedia", "https://www.linkedin.com/company/lifepalcoid/", "https://lifepal.co.id/"] })
          }}
        />
      </>
    );
  };

  return (
    <Head>
      <title>{title}</title>
      {/* <!-- Google Tag Manager --> */}
      {APP_ENV !== 'production' ? (
        <script
          dangerouslySetInnerHTML={{
            __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer', 'GTM-5744H9R');
              `,
          }}
          async
        />
      ) : (
        <script
          dangerouslySetInnerHTML={{
            __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer', 'GTM-PV336L7');
              `,
          }}
          async
        />
      )}
      {/* <!-- End Google Tag Manager --> */}
      {/* <!-- Google Tag Manager (noscript) --> */}
      {APP_ENV !== 'production' ? (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id='GTM-5744H9R'`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      ) : (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id='GTM-PV336L7'`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      )}
      {/* <!-- End Google Tag Manager (noscript) --> */}
        
      {/* <!-- FB pixel --> */}
      {APP_ENV == 'production' && (
        <>
          <script 
            dangerouslySetInnerHTML={{
              __html: `
                !function (f, b, e, v, n, t, s) {
                  if (f.fbq) return;
                  n = f.fbq = function () {
                    n.callMethod ?
                        n.callMethod.apply(n, arguments) : n.queue.push(arguments)
                  };
                  if (!f._fbq) f._fbq = n;
                  n.push = n;
                  n.loaded = !0;
                  n.version = '2.0';
                  n.queue = [];
                  t = b.createElement(e);
                  t.async = !0;
                  t.src = v;
                  s = b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t, s)
                }(window, document, 'script',
                    'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '1566246736845302');
                fbq('track', 'PageView');
              `,
            }}
            async
          />
          <noscript>
            <img height="1" width="1" style={{ display: 'none', visibility: 'hidden' }}
              src={`https://www.facebook.com/tr?id=1566246736845302&ev=PageView&noscript=1"`}
            />
          </noscript>
        </>
      )}
      {/* <!-- End FB pixel --> */}

      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      <meta name="theme-color" content="#1D66DD" />
      <meta name="mobile-web-app-capable" content="yes" />
      <link rel="canonical" key="canonical" href={url} />
      <link rel="shortcut icon" href="https://lifepal.co.id/static/images/favicon/favicon.ico" type="image/x-icon" />
      <link rel="apple-touch-icon" sizes="57x57" href="https://lifepal.co.id/static/images/favicon/apple-icon-57x57.png" />
      <link rel="apple-touch-icon" sizes="60x60" href="https://lifepal.co.id/static/images/favicon/apple-icon-60x60.png" />
      <link rel="apple-touch-icon" sizes="72x72" href="https://lifepal.co.id/static/images/favicon/apple-icon-72x72.png" />
      <link rel="apple-touch-icon" sizes="76x76" href="https://lifepal.co.id/static/images/favicon/apple-icon-76x76.png" />
      <link rel="apple-touch-icon" sizes="114x114" href="https://lifepal.co.id/static/images/favicon/apple-icon-114x114.png" />
      <link rel="apple-touch-icon" sizes="120x120" href="https://lifepal.co.id/static/images/favicon/apple-icon-120x120.png" />
      <link rel="apple-touch-icon" sizes="144x144" href="https://lifepal.co.id/static/images/favicon/apple-icon-144x144.png" />
      <link rel="apple-touch-icon" sizes="152x152" href="https://lifepal.co.id/static/images/favicon/apple-icon-152x152.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="https://lifepal.co.id/static/images/favicon/apple-icon-180x180.png" />
      <link rel="icon" type="image/png" sizes="192x192" href="https://lifepal.co.id/static/images/favicon/android-icon-192x192.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="https://lifepal.co.id/static/images/favicon/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="96x96" href="https://lifepal.co.id/static/images/favicon/favicon-96x96.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="https://lifepal.co.id/static/images/favicon/favicon-16x16.png" />
      <link
        href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,400;0,600;0,700;1,400&display=swap"
        rel="stylesheet"
      />
      <link rel="stylesheet" href="/media/static/css/all.min.css" />
      {cdnUrls[APP_ENV] && (
        <>
          <link rel="preconnect" href={cdnUrls[APP_ENV]} />
          <link rel="dns-prefetch" href={cdnUrls[APP_ENV]} />
        </>
      )}
      {APP_ENV !== 'production' && <meta name="robots" content="noindex, nofollow" />}
      {APP_ENV !== 'develop' && <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7440190460548738" crossOrigin="anonymous" />}
      {seoDetails()}
      {post &&
        featuredImages(post).length > 0 &&
        featuredImages(post).map((img, index) => (
          <link key={index} rel="preload" href={img} as="image" />
        ))}
    </Head>
  );
};
